import { ApiConfig } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import { GraphNode, GraphEdge, NodeType } from "./ontology-graph.model";
import {
  OntologyKpi,
  OntologyInitiativeRow,
  OntologyStatus
} from "./ontology.data";
import {
  CompetencyQuestionApiItem as OntologyCompetencyQuestionApiItem,
  CompetencyQuestionRow,
  CqStatus,
  GeneratedCqKpi
} from "./create-ontology-questions.data";
import {
  ConceptRow,
  AttributeRow,
  RelationshipRow,
  ConceptStatus,
  ConceptNodeType,
  EntitiesSummary,
  EntitiesSummaryByType,
  EntitiesRelationshipsNodeApiItem,
  EntitiesRelationshipsAttributeApiItem,
  EntitiesRelationshipsRelationshipApiItem
} from "./create-ontology-entities-relationships.data";
import {
  DecisionBadge,
  DecisionTone,
  ReuseDecisionRow,
  ReuseHarmonisationSummary
} from "./create-ontology-reuse-harmonisation.data";

// Base host for the Ontology API; defaults to the local FastAPI/uvicorn server when unset.
const ONTOLOGY_API_BASE_URL = process.env.REACT_APP_ENG_API_URL;
const ONTOLOGY_INITIATIVES_API_URL = `${ONTOLOGY_API_BASE_URL}/api/ontology/initiatives`;
// Bulk accept/reject act on competency questions directly by cqId, independent of any initiative.
const ONTOLOGY_COMPETENCY_QUESTIONS_API_URL = `${ONTOLOGY_API_BASE_URL}/api/ontology/competency-questions`;

export interface InitiativeFilters {
  searchName?: string;
  status?: string[];
  currentStage?: string[];
  owner?: string;
  businessUnitName?: string;
  teamName?: string;
}

// Query params supported by the backend's `GET /api/ontology/initiatives` endpoint.
export interface OntologyInitiativeQueryParams {
  search?: string;
  businessUnitName?: string;
  teamName?: string;
  statusName?: string;
  stageName?: string;
}

// Raw shape returned by the single `/initatives` endpoint, which serves KPI summary,
// recent activity and the initiative list all in one response.
export interface OntologyApiInitiative {
  initiativeId: string;
  initiativeName: string;
  businessAreaName: string[];
  businessObjective: string | null;
  businessValue: string | null;
  scope: string | null;
  exclusions: string | null;
  ownerName: string | null;
  businessUnitName: string | null;
  teamName: string | null;
  currentStageName: string;
  statusName: OntologyStatus;
  createdAt: string;
  updatedAt: string | null;
  lastUpdated: string;
}

export interface OntologySummary {
  activeInitiatives: number;
  draftInitiatives: number;
  awaitingReview: number;
  publishedOntologies: number;
}

// Real backend returns recent activity as {label, count} objects, not plain strings.
export interface OntologyApiRecentActivity {
  label: string;
  count: number;
}

export interface OntologyOverviewApiResponse {
  success: boolean;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: OntologySummary;
  recentActivity: OntologyApiRecentActivity[];
  businessUnitNames: string[];
  teamNames: string[];
  initiatives: OntologyApiInitiative[];
}

export interface OntologyOverview {
  kpis: OntologyKpi[];
  recentActivity: string[];
  businessUnitNames: string[];
  teamNames: string[];
  initiatives: OntologyInitiativeRow[];
}

// Real backend request/response for creating a new ontology initiative in one call
// (replaces the old mock `/initiatives` + `/capture_and_classify` two-call flow).
export interface CreateOntologyInitiativeRequest {
  initiativeName: string;
  businessAreaNames: string[];
  businessObjective: string;
  businessValue: string | null;
  scope: string | null;
  exclusions: string | null;
  retriggerFromCompetencyQuestion: boolean;
}

export interface CreateOntologyInitiativeResponse {
  success: boolean;
  initiativeId: string;
  statusName: string;
  currentStageName: string;
  message: string;
}

export interface OntologyInitiativeDetailResponse {
  success: boolean;
  initiative: OntologyApiInitiative;
}

export interface OntologyInitiativeDeleteResponse {
  success: boolean;
  initiativeId: string;
  message: string;
}

export interface OntologyBusinessQuestionItem {
  businessQuestionId: string;
  initiativeId: string;
  bqCode: string;
  questionText: string;
  status: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface OntologyBusinessQuestionListResponse {
  success: boolean;
  initiativeId: string;
  totalRecords: number;
  questions: OntologyBusinessQuestionItem[];
}

export interface OntologyBusinessQuestionRequest {
  questionText: string;
}

export interface OntologyBusinessQuestionUpsertResponse {
  success: boolean;
  question: OntologyBusinessQuestionItem;
  message: string;
}

export interface OntologyBusinessQuestionDeleteResponse {
  success: boolean;
  businessQuestionId: string;
  message: string;
}

export interface OntologyCompetencyQuestionSummary {
  totalGenerated: number;
  approved: number;
  needsReview: number;
  draft: number;
  rejected: number;
}

export interface OntologyCompetencyQuestionListResponse {
  success: boolean;
  initiativeId: string;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: OntologyCompetencyQuestionSummary;
  questions: OntologyCompetencyQuestionApiItem[];
}

// Response from `POST /competency-questions/trigger` — an async queue acknowledgement,
// not the generated questions themselves (fetch those separately via getCompetencyQuestions).
export interface OntologyCompetencyQuestionTriggerResponse {
  success: boolean;
  initiativeId: string;
  queueName: string;
  messageType: string;
  jobGuid: string | null;
  message: string;
}

// Response from `POST /entities/trigger` — an async queue acknowledgement; the generated
// concepts/attributes/relationships are fetched separately via getEntitiesRelationships.
export interface OntologyEntitiesGenerationTriggerResponse {
  success: boolean;
  initiativeId: string;
  queueName: string;
  messageType: string;
  jobGuid: string | null;
  message: string;
}

// Response from `POST /reuse-harmonize/trigger` — same async queue acknowledgement shape;
// the generated review rows are fetched separately via getReuseReviews.
export interface OntologyReuseHarmonizeTriggerResponse {
  success: boolean;
  initiativeId: string;
  queueName: string;
  messageType: string;
  jobGuid: string | null;
  message: string;
}

export interface OntologyNodeMatchApiItem {
  matchId: string;
  reviewId: string;
  sourceOntology: string;
  sourceNodeId: string | null;
  sourceNodeName: string;
  coverage: number;
  aiRecommendation: string;
  userDecision: string | null;
  rationale: string | null;
  isSelected: boolean;
  createdAt: string;
}

export interface OntologyReuseReviewApiItem {
  reviewId: string;
  initiativeId: string;
  nodeId: string;
  nodeName: string;
  nodeType: string | null;
  userDecision: string | null;
  aiRecommendedDecision: string | null;
  effectiveDecision: string;
  isDefaulted: boolean;
  needsDecision: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  matches: OntologyNodeMatchApiItem[];
}

export interface OntologyReuseSummaryApiResponse {
  reuseCount: number;
  harmoniseCount: number;
  addToInitiativeCount: number;
  assessedCount: number;
}

export interface OntologyReuseReviewListApiResponse {
  success: boolean;
  initiativeId: string;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: OntologyReuseSummaryApiResponse | null;
  reviews: OntologyReuseReviewApiItem[];
}

// Query params supported by `GET /reuse-reviews`.
export interface OntologyReuseReviewQueryFilters {
  nodeId?: string[];
  userDecision?: string[];
  aiRecommendation?: string[];
  needsDecision?: boolean;
  nodeType?: string[];
  businessArea?: string[];
  sourceOntology?: string[];
  minCoverage?: number;
  search?: string;
}

export interface OntologyNodeMatchRationaleApiResponse {
  success: boolean;
  matchId: string;
  reviewId: string;
  rationale: string | null;
}

export interface OntologyReuseDecisionItemRequest {
  reviewId: string;
  userDecision: string;
  matchId?: string;
}

export interface OntologyReuseBulkDecisionRequest {
  decisions: OntologyReuseDecisionItemRequest[];
  acceptAll?: boolean;
}

export interface OntologyReuseBulkDecisionResponse {
  success: boolean;
  appliedCount: number;
  autoAppliedCount: number;
  summary: OntologyReuseSummaryApiResponse;
}

export interface OntologyReuseDecisionResponse {
  success: boolean;
  reviewId: string;
  nodeId: string;
  userDecision: string;
  selectedMatchId: string | null;
  reuseHarmoniseStatus: string | null;
  reviewedAt: string;
}

export interface OntologyCompetencyQuestionBulkAcceptResponse {
  success: boolean;
  acceptedCount: number;
  acceptedCqIds: string[];
  notFoundCqIds: string[];
  message: string;
}

export interface OntologyCompetencyQuestionBulkRejectResponse {
  success: boolean;
  rejectedCount: number;
  rejectedCqIds: string[];
  notFoundCqIds: string[];
  message: string;
}

export interface OntologyCompetencyQuestionDeleteResponse {
  success: boolean;
  cqId: string;
  message: string;
}

export interface OntologyCompetencyQuestionUpsertResponse {
  success: boolean;
  question: OntologyCompetencyQuestionApiItem;
  message: string;
}

// Response shapes for the three separate per-type list endpoints (GET /nodes, /attributes,
// /relationships), which replaced the single combined /entities-relationships endpoint.
export interface OntologyNodesListResponse {
  success: boolean;
  initiativeId: string;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  nodeCount: number;
  summary: EntitiesSummary;
  nodes: EntitiesRelationshipsNodeApiItem[];
}

export interface OntologyAttributesListResponse {
  success: boolean;
  initiativeId: string;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  attributeCount: number;
  summary: EntitiesSummary;
  attributes: EntitiesRelationshipsAttributeApiItem[];
}

export interface OntologyRelationshipsListResponse {
  success: boolean;
  initiativeId: string;
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  relationshipCount: number;
  summary: EntitiesSummary;
  relationships: EntitiesRelationshipsRelationshipApiItem[];
}

// Query filters supported by GET /nodes, /attributes, /relationships.
export interface EntitiesRelationshipsQueryFilters {
  businessQuestionId?: string[];
  cqId?: string[];
  status?: string[];
  nodeType?: string[];
}

// Pagination state tracked per list (nodes/attributes/relationships) to drive infinite scroll.
export interface ListPaginationMeta {
  page: number;
  totalPages: number;
  totalRecords: number;
}

// entityType matches the backend's expected values for POST /entities/status.
export type EntitiesRelationshipsEntityType =
  | "nodes"
  | "attributes"
  | "relationships";
export type EntitiesRelationshipsStatusAction = "accept" | "reject";

export interface OntologyEntitiesStatusUpdateRequest {
  entityType: EntitiesRelationshipsEntityType;
  action: EntitiesRelationshipsStatusAction;
  ids: string[];
}

export interface OntologyEntitiesStatusUpdateResponse {
  success: boolean;
  message: string;
}

// entityType matches the backend's expected values for PATCH /entities (singular, unlike
// the plural EntitiesRelationshipsEntityType used by the bulk accept/reject endpoint).
export type EntityPatchType = "node" | "attribute" | "relationship";

export interface OntologyEntityPatchRequest {
  entityType: EntityPatchType;
  entityId: string;
  updates: Record<string, string>;
}

export interface OntologyEntityPatchResponse {
  success: boolean;
  message: string;
}

// Request body for POST /initiatives/{initiativeId}/entities (Add drawer "Create" actions).
// Fields are a superset across node/attribute/relationship; only the ones relevant to
// `entityType` need to be populated.
export interface OntologyEntityCreateRequest {
  entityType: EntityPatchType;
  businessQuestionIds: string[];
  cqIds: string[];
  description: string;
  status: string;
  // node fields
  nodeName?: string;
  nodeType?: string;
  businessArea?: string;
  rationale?: string;
  // attribute fields
  nodeId?: string;
  attributeName?: string;
  dataType?: string;
  // relationship fields
  sourceNodeId?: string;
  targetNodeId?: string;
  relationshipName?: string;
  cardinality?: string;
  direction?: string;
}

export interface OntologyEntityCreateResponse {
  success: boolean;
  entityId: string;
  message: string;
}

export interface OntologyNodeTraceabilityBusinessQuestion {
  businessQuestionId: string;
  bqCode: string;
  questionText: string;
}

export interface OntologyNodeTraceabilityCompetencyQuestion {
  cqId: string;
  cqCode: string;
  questionText: string;
}

export interface OntologyNodeDetailResponse {
  success: boolean;
  initiativeId: string;
  traceability: {
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  };
  node: EntitiesRelationshipsNodeApiItem;
}

// Raw shape returned by GET /initiatives/{initiativeId}/nodes/{nodeId}/related-entities —
// a node plus ALL of its own attributes/relationships (unlike the paginated, initiative-wide
// attributes/relationships tables).
export interface OntologyNodeRelatedEntitiesResponse {
  success: boolean;
  initiativeId: string;
  traceability: {
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  };
  node: EntitiesRelationshipsNodeApiItem;
  attributeCount: number;
  relationshipCount: number;
  attributes: EntitiesRelationshipsAttributeApiItem[];
  relationships: EntitiesRelationshipsRelationshipApiItem[];
}

export interface OntologyAttributeDetailResponse {
  success: boolean;
  initiativeId: string;
  traceability: {
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  };
  attribute: EntitiesRelationshipsAttributeApiItem;
}

export interface OntologyRelationshipDetailResponse {
  success: boolean;
  initiativeId: string;
  traceability: {
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  };
  relationship: EntitiesRelationshipsRelationshipApiItem;
}

// Raw shape returned by GET /api/ontology/nodes/{nodeId}/graph-view?includeAttributes=true
// (double-click "Related Entities" subgraph on the Graph View canvas).
export interface OntologyNodeGraphViewAttribute {
  attributeId: string;
  attributeName: string;
  dataType: string;
  cqCodes: string[];
  status: string;
  confidenceScore: number;
  description: string;
}

export interface OntologyNodeGraphViewNode {
  id: string;
  label: string;
  nodeId: string;
  nodeType: string;
  businessArea: string;
  status: string;
  connectionCount: number;
  confidenceScore: number;
  businessQuestionIds: string[];
  bqCodes: string[];
  cqIds: string[];
  cqCodes: string[];
  attributes: OntologyNodeGraphViewAttribute[];
  description: string;
}

export interface OntologyNodeGraphViewEdge {
  id: string;
  source: string;
  target: string;
  relationshipId: string;
  relationshipName: string;
  cardinality: string;
  direction: string;
  status: string;
  confidenceScore: number;
  businessQuestionIds: string[];
  bqCodes: string[];
  cqIds: string[];
  cqCodes: string[];
}

export interface OntologyNodeGraphViewResponse {
  success: boolean;
  initiativeId: string;
  nodeCount: number;
  edgeCount: number;
  attributeCount: number;
  nodes: OntologyNodeGraphViewNode[];
  edges: OntologyNodeGraphViewEdge[];
}

// Raw shape returned by GET /initiatives/{initiativeId}/graph-view.
export interface OntologyGraphViewApiNode {
  id?: string;
  nodeId?: string;
  label?: string;
  name?: string;
  nodeName?: string;
  type?: string;
  nodeType?: string;
}

export interface OntologyGraphViewApiEdge {
  id?: string;
  source: string;
  target: string;
  relationshipName: string;
}

export interface OntologyGraphViewApiResponse {
  success: boolean;
  initiativeId: string;
  nodes: OntologyGraphViewApiNode[];
  edges: OntologyGraphViewApiEdge[];
}

class OntologyApiClient extends HttpClient {
  // Maps to the backend's `?search=`, `?businessUnitName=`, `?teamName=`, `?statusName=`, `?stageName=` query parameters.
  // The backend paginates (`pageSize` defaults to 10 server-side), but the UI does its own
  // client-side filtering/pagination over the full list, so a large pageSize is requested here
  // to make sure every initiative is actually fetched instead of silently capping at 10.
  public static getInitiatives(params?: OntologyInitiativeQueryParams) {
    const query = new URLSearchParams({ page: "1", pageSize: "1000" });
    if (params?.search) query.set("search", params.search);
    if (params?.businessUnitName)
      query.set("businessUnitName", params.businessUnitName);
    if (params?.teamName) query.set("teamName", params.teamName);
    if (params?.statusName) query.set("statusName", params.statusName);
    if (params?.stageName) query.set("stageName", params.stageName);
    return this.invokeApi<OntologyOverviewApiResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}?${query.toString()}`,
        "GET"
      )
    );
  }

  public static createInitiative(payload: CreateOntologyInitiativeRequest) {
    return this.invokeApi<CreateOntologyInitiativeResponse>(
      new ApiConfig(ONTOLOGY_INITIATIVES_API_URL, "POST", "", payload)
    );
  }

  public static getInitiativeById(initiativeId: string) {
    return this.invokeApi<OntologyInitiativeDetailResponse>(
      new ApiConfig(`${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}`, "GET")
    );
  }

  public static updateInitiative(
    initiativeId: string,
    payload: CreateOntologyInitiativeRequest
  ) {
    return this.invokeApi<CreateOntologyInitiativeResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}`,
        "PUT",
        "",
        payload
      )
    );
  }

  public static deleteInitiative(initiativeId: string) {
    return this.invokeApi<OntologyInitiativeDeleteResponse>(
      new ApiConfig(`${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}`, "DELETE")
    );
  }

  public static getBusinessQuestions(initiativeId: string) {
    return this.invokeApi<OntologyBusinessQuestionListResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/business-questions`,
        "GET"
      )
    );
  }

  public static createBusinessQuestion(
    initiativeId: string,
    payload: OntologyBusinessQuestionRequest
  ) {
    return this.invokeApi<OntologyBusinessQuestionUpsertResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/business-questions`,
        "POST",
        "",
        payload
      )
    );
  }

  public static updateBusinessQuestion(
    initiativeId: string,
    businessQuestionId: string,
    payload: OntologyBusinessQuestionRequest
  ) {
    return this.invokeApi<OntologyBusinessQuestionUpsertResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/business-questions/${businessQuestionId}`,
        "PUT",
        "",
        payload
      )
    );
  }

  public static deleteBusinessQuestion(
    initiativeId: string,
    businessQuestionId: string
  ) {
    return this.invokeApi<OntologyBusinessQuestionDeleteResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/business-questions/${businessQuestionId}`,
        "DELETE"
      )
    );
  }

  // Publishes a CQ generation job to the backend queue; the actual questions are produced
  // asynchronously and must be fetched afterwards via getCompetencyQuestions.
  public static triggerCompetencyQuestionGeneration(
    initiativeId: string,
    generationMode: string,
    businessQuestionId?: string
  ) {
    // generationMode disabled for now; not sent to the backend.
    const query = new URLSearchParams();
    if (businessQuestionId) query.set("businessQuestionId", businessQuestionId);
    const queryString = query.toString();
    return this.invokeApi<OntologyCompetencyQuestionTriggerResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/competency-questions/trigger${
          queryString ? `?${queryString}` : ""
        }`,
        "POST",
        "",
        {}
      )
    );
  }

  public static getCompetencyQuestions(
    initiativeId: string,
    page: number,
    pageSize: number,
    source?: string[],
    status?: string[],
    bq?: string[]
  ) {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize)
    });
    // Backend expects repeated ?source=A&source=B / ?status=A&status=B / ?bq=A&bq=B params
    // rather than comma-joined values.
    if (source && source.length)
      source.forEach((value) => query.append("source", value));
    if (status && status.length)
      status.forEach((value) => query.append("status", value));
    if (bq && bq.length) bq.forEach((code) => query.append("bq", code));
    return this.invokeApi<OntologyCompetencyQuestionListResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/competency-questions?${query.toString()}`,
        "GET"
      )
    );
  }

  public static acceptCompetencyQuestions(cqIds: string[]) {
    return this.invokeApi<OntologyCompetencyQuestionBulkAcceptResponse>(
      new ApiConfig(
        `${ONTOLOGY_COMPETENCY_QUESTIONS_API_URL}/accept`,
        "POST",
        "",
        { cqIds }
      )
    );
  }

  public static rejectCompetencyQuestions(cqIds: string[]) {
    return this.invokeApi<OntologyCompetencyQuestionBulkRejectResponse>(
      new ApiConfig(
        `${ONTOLOGY_COMPETENCY_QUESTIONS_API_URL}/reject`,
        "POST",
        "",
        { cqIds }
      )
    );
  }

  public static deleteCompetencyQuestion(cqId: string) {
    return this.invokeApi<OntologyCompetencyQuestionDeleteResponse>(
      new ApiConfig(
        `${ONTOLOGY_COMPETENCY_QUESTIONS_API_URL}/${cqId}`,
        "DELETE"
      )
    );
  }

  public static updateCompetencyQuestion(
    initiativeId: string,
    cqId: string,
    questionText: string
  ) {
    return this.invokeApi<OntologyCompetencyQuestionUpsertResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/competency-questions/${cqId}`,
        "PUT",
        "",
        { questionText }
      )
    );
  }

  // Manually creates a single competency question for a specific business question
  // (as opposed to the async bulk generation queued via triggerCompetencyQuestionGeneration).
  public static createCompetencyQuestion(
    initiativeId: string,
    businessQuestionId: string,
    questionText: string
  ) {
    return this.invokeApi<OntologyCompetencyQuestionUpsertResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/business-questions/${businessQuestionId}/competency-questions`,
        "POST",
        "",
        { questionText }
      )
    );
  }

  // Fetches only the generated Concepts (nodes) for an initiative, with their own per-type
  // summary, optionally filtered by Business Question, Competency Question, status, node type.
  public static getNodes(
    initiativeId: string,
    filters?: EntitiesRelationshipsQueryFilters,
    page = 1
  ) {
    const query = new URLSearchParams({ pageSize: "100", page: String(page) });
    if (filters?.businessQuestionId && filters.businessQuestionId.length)
      query.set("businessQuestionId", filters.businessQuestionId.join(","));
    if (filters?.cqId && filters.cqId.length)
      query.set("cqId", filters.cqId.join(","));
    if (filters?.status && filters.status.length)
      query.set("status", filters.status.join(","));
    if (filters?.nodeType && filters.nodeType.length)
      query.set("nodeType", filters.nodeType.join(","));
    const queryString = query.toString();
    return this.invokeApi<OntologyNodesListResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/nodes${
          queryString ? `?${queryString}` : ""
        }`,
        "GET"
      )
    );
  }

  // Fetches only the generated Attributes for an initiative, with their own per-type summary,
  // optionally filtered by Business Question, Competency Question, review status.
  public static getAttributes(
    initiativeId: string,
    filters?: EntitiesRelationshipsQueryFilters,
    page = 1
  ) {
    const query = new URLSearchParams({ pageSize: "100", page: String(page) });
    if (filters?.businessQuestionId && filters.businessQuestionId.length)
      query.set("businessQuestionId", filters.businessQuestionId.join(","));
    if (filters?.cqId && filters.cqId.length)
      query.set("cqId", filters.cqId.join(","));
    if (filters?.status && filters.status.length)
      query.set("status", filters.status.join(","));
    const queryString = query.toString();
    return this.invokeApi<OntologyAttributesListResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/attributes${
          queryString ? `?${queryString}` : ""
        }`,
        "GET"
      )
    );
  }

  // Fetches only the generated Relationships for an initiative, with their own per-type
  // summary, optionally filtered by Business Question, Competency Question, review status.
  public static getRelationships(
    initiativeId: string,
    filters?: EntitiesRelationshipsQueryFilters,
    page = 1
  ) {
    const query = new URLSearchParams({ pageSize: "100", page: String(page) });
    if (filters?.businessQuestionId && filters.businessQuestionId.length)
      query.set("businessQuestionId", filters.businessQuestionId.join(","));
    if (filters?.cqId && filters.cqId.length)
      query.set("cqId", filters.cqId.join(","));
    if (filters?.status && filters.status.length)
      query.set("status", filters.status.join(","));
    const queryString = query.toString();
    return this.invokeApi<OntologyRelationshipsListResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/relationships${
          queryString ? `?${queryString}` : ""
        }`,
        "GET"
      )
    );
  }

  // Bulk accepts/rejects nodes, attributes, or relationships by id.
  public static updateEntitiesStatus(
    initiativeId: string,
    payload: OntologyEntitiesStatusUpdateRequest
  ) {
    return this.invokeApi<OntologyEntitiesStatusUpdateResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/entities/status`,
        "POST",
        "",
        payload
      )
    );
  }

  // Fetches a single node plus its Business/Competency Question traceability (View drawer).
  public static getNodeDetail(initiativeId: string, nodeId: string) {
    return this.invokeApi<OntologyNodeDetailResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/nodes/${nodeId}`,
        "GET"
      )
    );
  }

  // Fetches a single node plus ALL of its own attributes/relationships — the correct source
  // for a node's Attributes list (the client-side attributeRows table only ever holds one
  // page of ALL attributes across the whole initiative, so name-matching against it misses
  // attributes for most nodes).
  public static getNodeRelatedEntities(initiativeId: string, nodeId: string) {
    return this.invokeApi<OntologyNodeRelatedEntitiesResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/nodes/${nodeId}/related-entities`,
        "GET"
      )
    );
  }

  // Fetches a single attribute plus its Business/Competency Question traceability (View drawer).
  public static getAttributeDetail(initiativeId: string, attributeId: string) {
    return this.invokeApi<OntologyAttributeDetailResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/attributes/${attributeId}`,
        "GET"
      )
    );
  }

  // Fetches a single relationship plus its Business/Competency Question traceability (View drawer).
  public static getRelationshipDetail(
    initiativeId: string,
    relationshipId: string
  ) {
    return this.invokeApi<OntologyRelationshipDetailResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/relationships/${relationshipId}`,
        "GET"
      )
    );
  }

  // Fetches a node's subgraph (itself + directly connected nodes/edges), for the
  // double-click "Related Entities" view on the Graph View canvas.
  public static getNodeGraphView(nodeId: string) {
    return this.invokeApi<OntologyNodeGraphViewResponse>(
      new ApiConfig(
        `${ONTOLOGY_API_BASE_URL}/api/ontology/nodes/${nodeId}/graph-view?includeAttributes=true`,
        "GET"
      )
    );
  }

  // Fetches the full Concepts/Relationships graph for an initiative (Graph View tab).
  public static getGraphView(initiativeId: string) {
    return this.invokeApi<OntologyGraphViewApiResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/graph-view`,
        "GET"
      )
    );
  }

  // Publishes an entities-generation job to the backend queue; the generated concepts/
  // attributes/relationships must be fetched afterwards via getEntitiesRelationships.
  public static triggerEntitiesGeneration(initiativeId: string) {
    return this.invokeApi<OntologyEntitiesGenerationTriggerResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/entities/trigger`,
        "POST",
        "",
        {}
      )
    );
  }

  // Patches a single node/attribute/relationship's fields (Modify drawer "Apply Changes").
  public static updateEntity(
    initiativeId: string,
    payload: OntologyEntityPatchRequest
  ) {
    return this.invokeApi<OntologyEntityPatchResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/entities`,
        "PATCH",
        "",
        payload
      )
    );
  }

  // Creates a new node/attribute/relationship (Add drawer "Create" actions).
  public static createEntity(
    initiativeId: string,
    payload: OntologyEntityCreateRequest
  ) {
    return this.invokeApi<OntologyEntityCreateResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/entities`,
        "POST",
        "",
        payload
      )
    );
  }

  // Publishes a reuse/harmonisation analysis job to the backend queue; the generated review
  // rows must be fetched afterwards via getReuseReviews.
  public static triggerReuseHarmonizeAnalysis(initiativeId: string) {
    return this.invokeApi<OntologyReuseHarmonizeTriggerResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/reuse-harmonize/trigger`,
        "POST",
        "",
        {}
      )
    );
  }

  // Lists reuse/harmonisation review rows (+ their candidate matches) for the Decision Matrix.
  public static getReuseReviews(
    initiativeId: string,
    page: number,
    pageSize: number,
    filters?: OntologyReuseReviewQueryFilters
  ) {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize)
    });
    if (filters?.nodeId?.length)
      filters.nodeId.forEach((value) => query.append("nodeId", value));
    if (filters?.userDecision?.length)
      filters.userDecision.forEach((value) =>
        query.append("userDecision", value)
      );
    if (filters?.aiRecommendation?.length)
      filters.aiRecommendation.forEach((value) =>
        query.append("aiRecommendation", value)
      );
    if (filters?.needsDecision != null)
      query.set("needsDecision", String(filters.needsDecision));
    if (filters?.nodeType?.length)
      filters.nodeType.forEach((value) => query.append("nodeType", value));
    if (filters?.businessArea?.length)
      filters.businessArea.forEach((value) =>
        query.append("businessArea", value)
      );
    if (filters?.sourceOntology?.length)
      filters.sourceOntology.forEach((value) =>
        query.append("sourceOntology", value)
      );
    if (filters?.minCoverage != null)
      query.set("minCoverage", String(filters.minCoverage));
    if (filters?.search) query.set("search", filters.search);
    return this.invokeApi<OntologyReuseReviewListApiResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/reuse-reviews?${query.toString()}`,
        "GET"
      )
    );
  }

  // Returns only the rationale text for a single candidate match (used by the history drawer
  // when a match's rationale wasn't already included in the reuse-reviews list response).
  public static getReuseMatchRationale(initiativeId: string, matchId: string) {
    return this.invokeApi<OntologyNodeMatchRationaleApiResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/reuse-matches/${matchId}/rationale`,
        "GET"
      )
    );
  }

  // Applies explicit decisions in bulk; with acceptAll, auto-applies the AI recommendation to
  // every undecided row.
  public static setReuseDecisionsBulk(
    initiativeId: string,
    payload: OntologyReuseBulkDecisionRequest
  ) {
    return this.invokeApi<OntologyReuseBulkDecisionResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/reuse-reviews/decisions`,
        "PUT",
        "",
        payload
      )
    );
  }

  // Sets Reuse/Harmonise (with matchId) or Add to Initiative for a single review row.
  public static setReuseDecision(
    initiativeId: string,
    reviewId: string,
    userDecision: string,
    matchId?: string
  ) {
    return this.invokeApi<OntologyReuseDecisionResponse>(
      new ApiConfig(
        `${ONTOLOGY_INITIATIVES_API_URL}/${initiativeId}/reuse-reviews/${reviewId}/decision`,
        "PUT",
        "",
        { userDecision, matchId }
      )
    );
  }
}

const mapApiInitiativeToRow = (
  raw: OntologyApiInitiative
): OntologyInitiativeRow => ({
  id: raw.initiativeId,
  name: raw.initiativeName,
  owner: raw.ownerName ?? "Unassigned",
  currentStage: raw.currentStageName,
  businessUnitName: raw.businessUnitName ?? "Unassigned",
  teamName: raw.teamName ?? "Unassigned",
  lastUpdated: raw.lastUpdated,
  status: raw.statusName
});

// Backend uses uppercase review statuses (APPROVED/REJECTED); anything else generated by the
// queue worker (e.g. PENDING) hasn't been reviewed yet, so it maps to "Needs Review".
const CQ_STATUS_MAP: Record<string, CqStatus> = {
  APPROVED: "Approved",
  REJECTED: "Rejected",
  DRAFT: "Draft"
};

const mapApiCqToRow = (
  raw: OntologyCompetencyQuestionApiItem
): CompetencyQuestionRow => ({
  id: raw.cqId,
  cqId: raw.cqCode,
  initiativeId: raw.initiativeId,
  businessQuestionId: raw.businessQuestionId,
  businessQuestionCode: raw.businessQuestionCode,
  businessQuestionText: raw.businessQuestionText,
  question: raw.questionText,
  confidence: raw.confidenceScore != null ? Math.round(raw.confidenceScore) : 0,
  confidenceScore: raw.confidenceScore,
  status: CQ_STATUS_MAP[raw.status?.toUpperCase()] ?? "Needs Review",
  source: raw.source,
  explanation: raw.explanation,
  evidenceSource: raw.evidenceSource,
  metadata: raw.metadata,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
  reviewedAt: raw.reviewedAt
});

// Backend entity statuses are "Draft"/"Accepted"/"Rejected" (see ontology_entities_service.py);
// anything else (e.g. an explicit "Needs Review") falls back to "Needs Review".
const mapEntityStatus = (status: string): ConceptStatus => {
  const normalized = status?.toUpperCase();
  if (normalized === "ACCEPTED" || normalized === "APPROVED") return "Accepted";
  if (normalized === "REJECTED") return "Rejected";
  if (normalized === "DRAFT") return "Draft";
  return "Needs Review";
};

const mapEntityNodeType = (nodeType: string): ConceptNodeType =>
  nodeType?.toUpperCase().includes("DOC") ? "Document" : "Class";

const mapApiNodeToConceptRow = (
  raw: EntitiesRelationshipsNodeApiItem
): ConceptRow => ({
  id: raw.nodeId,
  cqId: raw.cqIds[0] ?? "",
  cqCode: raw.cqCodes.length ? raw.cqCodes.join(", ") : "-",
  cqCodes: raw.cqCodes,
  bqCode: raw.bqCodes.length ? raw.bqCodes.join(", ") : "-",
  bqCodes: raw.bqCodes,
  nodeName: raw.nodeName,
  nodeType: mapEntityNodeType(raw.nodeType),
  nodeTypeRaw: raw.nodeType,
  description: raw.description,
  confidenceScore:
    raw.confidenceScore != null ? Math.round(raw.confidenceScore) : null,
  status: mapEntityStatus(raw.status)
});

// nodeNameById resolves the attribute's/relationship's nodeId references to a display name,
// since the backend only returns ids for those links.
const mapApiAttributeToRow = (
  raw: EntitiesRelationshipsAttributeApiItem,
  nodeNameById: Map<string, string>
): AttributeRow => ({
  id: raw.attributeId,
  cqId: raw.cqIds[0] ?? "",
  cqCode: raw.cqCodes.length ? raw.cqCodes.join(", ") : "-",
  cqCodes: raw.cqCodes,
  bqCode: raw.bqCodes.length ? raw.bqCodes.join(", ") : "-",
  bqCodes: raw.bqCodes,
  nodeName: nodeNameById.get(raw.nodeId) ?? raw.nodeId,
  attribute: raw.attributeName,
  description: raw.description,
  confidenceScore:
    raw.confidenceScore != null ? Math.round(raw.confidenceScore) : null,
  status: mapEntityStatus(raw.status)
});

const mapApiRelationshipToRow = (
  raw: EntitiesRelationshipsRelationshipApiItem,
  nodeNameById: Map<string, string>
): RelationshipRow => ({
  id: raw.relationshipId,
  cqId: raw.cqIds[0] ?? "",
  cqCode: raw.cqCodes.length ? raw.cqCodes.join(", ") : "-",
  cqCodes: raw.cqCodes,
  bqCode: raw.bqCodes.length ? raw.bqCodes.join(", ") : "-",
  bqCodes: raw.bqCodes,
  sourceNode: nodeNameById.get(raw.sourceNodeId) ?? raw.sourceNodeId,
  relationshipType: raw.relationshipName,
  targetNode: nodeNameById.get(raw.targetNodeId) ?? raw.targetNodeId,
  description: raw.description,
  confidenceScore:
    raw.confidenceScore != null ? Math.round(raw.confidenceScore) : null,
  status: mapEntityStatus(raw.status)
});

// Reuse/harmonise decision labels map to a fixed badge tone; anything unrecognized (including
// null, i.e. not yet decided) renders as a neutral "Pending" badge.
const REUSE_DECISION_TONE: Record<string, DecisionTone> = {
  Reuse: "green",
  Harmonise: "blue",
  "Add to Initiative": "red"
};

const decisionToBadge = (decision: string | null | undefined): DecisionBadge =>
  decision && REUSE_DECISION_TONE[decision]
    ? { label: decision, tone: REUSE_DECISION_TONE[decision] }
    : { label: "Pending", tone: "grey" };

// Maps a reuse-review API row (+ its candidate matches) to the Decision Matrix's row shape.
// "Source Ontology"/"Confidence" show the selected match if the row already has a decision,
// otherwise the top (first) candidate match, since a review can carry several candidates.
const mapReuseReviewToRow = (
  raw: OntologyReuseReviewApiItem
): ReuseDecisionRow => {
  const topMatch =
    raw.matches.find((match) => match.isSelected) ?? raw.matches[0];
  return {
    id: raw.reviewId,
    conceptName: raw.nodeName,
    conceptCategory: raw.nodeType ?? "—",
    sourceOntology: topMatch?.sourceOntology ?? "—",
    confidencePercent: topMatch ? Math.round(topMatch.coverage) : 0,
    proposedDecision: decisionToBadge(raw.aiRecommendedDecision),
    currentDecision: decisionToBadge(raw.userDecision),
    reviewId: raw.reviewId,
    nodeId: raw.nodeId,
    selectedMatchId: topMatch?.matchId ?? null,
    matches: raw.matches.map((match) => ({
      matchId: match.matchId,
      sourceOntology: match.sourceOntology,
      sourceNodeName: match.sourceNodeName,
      coverage: match.coverage,
      aiRecommendation: match.aiRecommendation,
      rationale: match.rationale,
      isSelected: match.isSelected
    }))
  };
};

const mapReuseSummary = (
  raw: OntologyReuseSummaryApiResponse | null,
  totalRecords: number,
  reviews: OntologyReuseReviewApiItem[]
): ReuseHarmonisationSummary => {
  const ontologySources = new Set<string>();
  reviews.forEach((review) =>
    review.matches.forEach((match) => ontologySources.add(match.sourceOntology))
  );
  return {
    reuseExisting: raw?.reuseCount ?? 0,
    harmoniseExtend: raw?.harmoniseCount ?? 0,
    ontologiesReferenced: ontologySources.size,
    conceptsAssessed: raw?.assessedCount ?? totalRecords,
    ontologySourcesCount: ontologySources.size
  };
};

// Arranges nodes on a circle since the graph-view API doesn't provide coordinates.
const circularGraphLayout = (
  ids: string[]
): Record<string, { x: number; y: number }> => {
  const positions: Record<string, { x: number; y: number }> = {};
  const count = ids.length;
  if (count === 0) return positions;
  if (count === 1) {
    positions[ids[0]] = { x: 400, y: 300 };
    return positions;
  }
  const radius = Math.max(260, (count * 160) / (2 * Math.PI));
  const center = radius + 120;
  ids.forEach((id, index) => {
    const angle = (2 * Math.PI * index) / count - Math.PI / 2;
    positions[id] = {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  });
  return positions;
};

// Maps the raw graph-view response into the render-ready shape GraphCanvas expects.
// Edges only carry source/target node ids + the relationship label; node labels are
// looked up from the nodes list so each edge renders as source --[relationshipName]--> target.
const mapGraphViewResponse = (
  data: OntologyGraphViewApiResponse
): { nodes: GraphNode[]; edges: GraphEdge[] } => {
  const rawNodes = data.nodes ?? [];
  const rawEdges = data.edges ?? [];
  const nodeIds = rawNodes.map((node) => node.id ?? node.nodeId ?? "");
  const positions = circularGraphLayout(nodeIds);

  const nodes: GraphNode[] = rawNodes.map((node) => {
    const id = node.id ?? node.nodeId ?? "";
    const type = node.nodeType ?? node.type ?? "";
    return {
      id,
      label: node.label ?? node.nodeName ?? node.name ?? id,
      // Keep the actual nodeType string (not collapsed to Document/Class) so every
      // node of the same type gets the same colour in the graph view.
      type: type || NodeType.ASSET,
      x: positions[id]?.x ?? 0,
      y: positions[id]?.y ?? 0
    };
  });

  const edges: GraphEdge[] = rawEdges.map((edge, index) => ({
    id: edge.id ?? `${edge.source}__${edge.target}__${index}`,
    source: edge.source,
    target: edge.target,
    relationship: edge.relationshipName
  }));

  return { nodes, edges };
};

const formatRecentActivity = (items: OntologyApiRecentActivity[]): string[] =>
  items.map((item) => `${item.label} (${item.count})`);

const mapSummaryToKpis = (summary: OntologySummary): OntologyKpi[] => [
  {
    id: "active",
    label: "Active Initiatives",
    value: String(summary.activeInitiatives),
    delta: "",
    accent: "neutral"
  },
  {
    id: "draft",
    label: "Draft Initiatives",
    value: String(summary.draftInitiatives),
    delta: "",
    accent: "neutral"
  },
  {
    id: "awaiting-review",
    label: "Awaiting Review",
    value: String(summary.awaitingReview),
    delta: "",
    accent: "neutral"
  },
  {
    id: "published",
    label: "Published Ontologies",
    value: String(summary.publishedOntologies),
    delta: "",
    accent: "neutral"
  }
];

// Applies Initiative Name search + Status/Stage/Owner/Business Unit/Team filters client-side,
// since the combined `/initatives` endpoint returns its full dataset in a single response.
export const filterInitiatives = (
  rows: OntologyInitiativeRow[],
  filters: InitiativeFilters
): OntologyInitiativeRow[] => {
  const searchTerm = filters.searchName?.trim().toLowerCase();
  return rows.filter((row) => {
    if (searchTerm && !row.name.toLowerCase().includes(searchTerm)) {
      return false;
    }
    if (filters.status?.length && !filters.status.includes(row.status)) {
      return false;
    }
    if (
      filters.currentStage?.length &&
      !filters.currentStage.includes(row.currentStage)
    ) {
      return false;
    }
    if (
      filters.owner &&
      filters.owner !== "All" &&
      row.owner !== filters.owner
    ) {
      return false;
    }
    if (
      filters.businessUnitName &&
      filters.businessUnitName !== "All" &&
      row.businessUnitName !== filters.businessUnitName
    ) {
      return false;
    }
    if (
      filters.teamName &&
      filters.teamName !== "All" &&
      row.teamName !== filters.teamName
    ) {
      return false;
    }
    return true;
  });
};

export const OntologyService = {
  // Single endpoint serving KPI summary, recent activity and the full initiative list together.
  // Fetches the first page only from the local FastAPI/uvicorn server.
  getOntologyOverview: async (): Promise<OntologyOverview> => {
    const response = await OntologyApiClient.getInitiatives();
    const data = response.value;
    return {
      kpis: mapSummaryToKpis(data.summary),
      recentActivity: formatRecentActivity(data.recentActivity),
      businessUnitNames: data.businessUnitNames,
      teamNames: data.teamNames,
      initiatives: data.initiatives.map(mapApiInitiativeToRow)
    };
  },

  // Backend-driven search + Business Unit/Team filtering for the Ontology Studio table.
  getFilteredInitiatives: async (
    params: OntologyInitiativeQueryParams
  ): Promise<OntologyInitiativeRow[]> => {
    const response = await OntologyApiClient.getInitiatives(params);
    return response.value.initiatives.map(mapApiInitiativeToRow);
  },

  // Persists a new initiative in a single real-backend call (used by the Create Ontology
  // Initiative wizard's Save Draft/Continue actions).
  createOntologyInitiative: async (
    payload: CreateOntologyInitiativeRequest
  ): Promise<CreateOntologyInitiativeResponse> => {
    const response = await OntologyApiClient.createInitiative(payload);
    return response.value;
  },

  // Fetches a single initiative by id (used to prefill the Setup page in edit mode).
  getInitiativeById: async (
    initiativeId: string
  ): Promise<OntologyApiInitiative> => {
    const response = await OntologyApiClient.getInitiativeById(initiativeId);
    return response.value.initiative;
  },

  // Updates an existing initiative in place (Save Draft/Continue when editing).
  updateOntologyInitiative: async (
    initiativeId: string,
    payload: CreateOntologyInitiativeRequest
  ): Promise<CreateOntologyInitiativeResponse> => {
    const response = await OntologyApiClient.updateInitiative(
      initiativeId,
      payload
    );
    return response.value;
  },

  // Deletes an ontology initiative by id.
  deleteOntologyInitiative: async (
    initiativeId: string
  ): Promise<OntologyInitiativeDeleteResponse> => {
    const response = await OntologyApiClient.deleteInitiative(initiativeId);
    return response.value;
  },

  // Lists the real business questions recorded for an initiative (Competency Questions stage).
  getOntologyBusinessQuestions: async (
    initiativeId: string
  ): Promise<OntologyBusinessQuestionItem[]> => {
    const response = await OntologyApiClient.getBusinessQuestions(initiativeId);
    return response.value.questions;
  },

  // Creates a new business question against the real backend.
  addOntologyBusinessQuestion: async (
    initiativeId: string,
    questionText: string
  ): Promise<OntologyBusinessQuestionItem> => {
    const response = await OntologyApiClient.createBusinessQuestion(
      initiativeId,
      { questionText }
    );
    return response.value.question;
  },

  // Updates the text of an existing business question against the real backend.
  updateOntologyBusinessQuestion: async (
    initiativeId: string,
    businessQuestionId: string,
    questionText: string
  ): Promise<OntologyBusinessQuestionItem> => {
    const response = await OntologyApiClient.updateBusinessQuestion(
      initiativeId,
      businessQuestionId,
      { questionText }
    );
    return response.value.question;
  },

  // Deletes a business question against the real backend.
  deleteOntologyBusinessQuestion: async (
    initiativeId: string,
    businessQuestionId: string
  ): Promise<OntologyBusinessQuestionDeleteResponse> => {
    const response = await OntologyApiClient.deleteBusinessQuestion(
      initiativeId,
      businessQuestionId
    );
    return response.value;
  },

  // Queues competency question generation for an initiative. Fire-and-forget: the actual
  // questions land asynchronously and must be fetched afterwards via getCompetencyQuestions.
  triggerCompetencyQuestionGeneration: async (
    initiativeId: string,
    generationMode = "full",
    businessQuestionId?: string
  ): Promise<OntologyCompetencyQuestionTriggerResponse> => {
    const response =
      await OntologyApiClient.triggerCompetencyQuestionGeneration(
        initiativeId,
        generationMode,
        businessQuestionId
      );
    return response.value;
  },

  // Queues entities (Concepts/Attributes/Relationships) generation for an initiative.
  // Fire-and-forget: poll getInitiativeById/getEntitiesRelationships afterwards.
  triggerEntitiesGeneration: async (
    initiativeId: string
  ): Promise<OntologyEntitiesGenerationTriggerResponse> => {
    const response = await OntologyApiClient.triggerEntitiesGeneration(
      initiativeId
    );
    return response.value;
  },

  // Fetches one page of competency questions for an initiative (post-generation review table,
  // driven by the backend's page/pageSize/totalPages pagination), optionally filtered by
  // source, status, and/or BQ ID (?bq=). The KPI summary cards come straight from the backend's
  // `summary` object rather than being derived from the (possibly partial/filtered) loaded rows.
  getCompetencyQuestions: async (
    initiativeId: string,
    page: number,
    pageSize: number,
    source?: string[],
    status?: string[],
    bq?: string[]
  ): Promise<{
    rows: CompetencyQuestionRow[];
    totalRecords: number;
    page: number;
    pageSize: number;
    totalPages: number;
    summaryKpis: GeneratedCqKpi[];
  }> => {
    const response = await OntologyApiClient.getCompetencyQuestions(
      initiativeId,
      page,
      pageSize,
      source,
      status,
      bq
    );
    const summary = response.value.summary;
    return {
      rows: response.value.questions.map(mapApiCqToRow),
      totalRecords: response.value.totalRecords,
      page: response.value.page,
      pageSize: response.value.pageSize,
      totalPages: response.value.totalPages,
      summaryKpis: [
        {
          id: "total-generated",
          label: "Total Generated",
          value: summary?.totalGenerated ?? 0
        },
        { id: "approved", label: "Approved", value: summary?.approved ?? 0 },
        {
          id: "needs-review",
          label: "Needs Review",
          value: summary?.needsReview ?? 0
        },
        { id: "draft", label: "Draft", value: summary?.draft ?? 0 },
        { id: "rejected", label: "Rejected", value: summary?.rejected ?? 0 }
      ]
    };
  },

  // Bulk-accepts the given competency questions by cqId.
  acceptCompetencyQuestions: async (
    cqIds: string[]
  ): Promise<OntologyCompetencyQuestionBulkAcceptResponse> => {
    const response = await OntologyApiClient.acceptCompetencyQuestions(cqIds);
    return response.value;
  },

  // Bulk-rejects the given competency questions by cqId.
  rejectCompetencyQuestions: async (
    cqIds: string[]
  ): Promise<OntologyCompetencyQuestionBulkRejectResponse> => {
    const response = await OntologyApiClient.rejectCompetencyQuestions(cqIds);
    return response.value;
  },

  // Soft-deletes a single competency question by cqId.
  deleteCompetencyQuestion: async (
    cqId: string
  ): Promise<OntologyCompetencyQuestionDeleteResponse> => {
    const response = await OntologyApiClient.deleteCompetencyQuestion(cqId);
    return response.value;
  },

  // Persists the edited question text for a competency question (Edit drawer save).
  updateCompetencyQuestion: async (
    initiativeId: string,
    cqId: string,
    questionText: string
  ): Promise<CompetencyQuestionRow> => {
    const response = await OntologyApiClient.updateCompetencyQuestion(
      initiativeId,
      cqId,
      questionText
    );
    return mapApiCqToRow(response.value.question);
  },

  // Manually adds a single competency question for a specific business question
  // (Add Competency Question drawer).
  createCompetencyQuestion: async (
    initiativeId: string,
    businessQuestionId: string,
    questionText: string
  ): Promise<CompetencyQuestionRow> => {
    const response = await OntologyApiClient.createCompetencyQuestion(
      initiativeId,
      businessQuestionId,
      questionText
    );
    return mapApiCqToRow(response.value.question);
  },

  // Fetches the generated Concepts/Attributes/Relationships for an initiative (Entities &
  // Relationships stage) via three separate per-type endpoints, optionally filtered by
  // Business Question/Competency Question/status/type. Returns page 1 of each list plus
  // pagination metadata so the caller can page further via getMoreNodes/getMoreAttributes/
  // getMoreRelationships as the user scrolls.
  getEntitiesRelationships: async (
    initiativeId: string,
    filters?: EntitiesRelationshipsQueryFilters
  ): Promise<{
    conceptRows: ConceptRow[];
    attributeRows: AttributeRow[];
    relationshipRows: RelationshipRow[];
    summary: EntitiesSummaryByType;
    pagination: {
      nodes: ListPaginationMeta;
      attributes: ListPaginationMeta;
      relationships: ListPaginationMeta;
    };
  }> => {
    const [nodesResponse, attributesResponse, relationshipsResponse] =
      await Promise.all([
        OntologyApiClient.getNodes(initiativeId, filters),
        OntologyApiClient.getAttributes(initiativeId, filters),
        OntologyApiClient.getRelationships(initiativeId, filters)
      ]);
    const nodes = nodesResponse.value.nodes;
    const attributes = attributesResponse.value.attributes;
    const relationships = relationshipsResponse.value.relationships;
    const nodeNameById = new Map(
      nodes.map((node) => [node.nodeId, node.nodeName])
    );
    return {
      conceptRows: nodes.map((node) => mapApiNodeToConceptRow(node)),
      attributeRows: attributes.map((item) =>
        mapApiAttributeToRow(item, nodeNameById)
      ),
      relationshipRows: relationships.map((item) =>
        mapApiRelationshipToRow(item, nodeNameById)
      ),
      summary: {
        nodes: nodesResponse.value.summary,
        attributes: attributesResponse.value.summary,
        relationships: relationshipsResponse.value.summary
      },
      pagination: {
        nodes: {
          page: nodesResponse.value.page,
          totalPages: nodesResponse.value.totalPages,
          totalRecords: nodesResponse.value.totalRecords
        },
        attributes: {
          page: attributesResponse.value.page,
          totalPages: attributesResponse.value.totalPages,
          totalRecords: attributesResponse.value.totalRecords
        },
        relationships: {
          page: relationshipsResponse.value.page,
          totalPages: relationshipsResponse.value.totalPages,
          totalRecords: relationshipsResponse.value.totalRecords
        }
      }
    };
  },

  // Fetches one additional page of Concepts/Attributes/Relationships for infinite scroll;
  // the attribute/relationship mappers need nodeNameById to resolve node references, built
  // by the caller from the Concepts already loaded so far.
  getMoreNodes: async (
    initiativeId: string,
    page: number,
    filters?: EntitiesRelationshipsQueryFilters
  ): Promise<{ conceptRows: ConceptRow[] } & ListPaginationMeta> => {
    const response = await OntologyApiClient.getNodes(
      initiativeId,
      filters,
      page
    );
    return {
      conceptRows: response.value.nodes.map((node) =>
        mapApiNodeToConceptRow(node)
      ),
      page: response.value.page,
      totalPages: response.value.totalPages,
      totalRecords: response.value.totalRecords
    };
  },

  getMoreAttributes: async (
    initiativeId: string,
    page: number,
    filters: EntitiesRelationshipsQueryFilters | undefined,
    nodeNameById: Map<string, string>
  ): Promise<{ attributeRows: AttributeRow[] } & ListPaginationMeta> => {
    const response = await OntologyApiClient.getAttributes(
      initiativeId,
      filters,
      page
    );
    return {
      attributeRows: response.value.attributes.map((item) =>
        mapApiAttributeToRow(item, nodeNameById)
      ),
      page: response.value.page,
      totalPages: response.value.totalPages,
      totalRecords: response.value.totalRecords
    };
  },

  getMoreRelationships: async (
    initiativeId: string,
    page: number,
    filters: EntitiesRelationshipsQueryFilters | undefined,
    nodeNameById: Map<string, string>
  ): Promise<{ relationshipRows: RelationshipRow[] } & ListPaginationMeta> => {
    const response = await OntologyApiClient.getRelationships(
      initiativeId,
      filters,
      page
    );
    return {
      relationshipRows: response.value.relationships.map((item) =>
        mapApiRelationshipToRow(item, nodeNameById)
      ),
      page: response.value.page,
      totalPages: response.value.totalPages,
      totalRecords: response.value.totalRecords
    };
  },

  // Bulk accepts/rejects the given Concepts/Attributes/Relationships by id.
  updateEntitiesStatus: async (
    initiativeId: string,
    entityType: EntitiesRelationshipsEntityType,
    action: EntitiesRelationshipsStatusAction,
    ids: string[]
  ): Promise<OntologyEntitiesStatusUpdateResponse> => {
    const response = await OntologyApiClient.updateEntitiesStatus(
      initiativeId,
      { entityType, action, ids }
    );
    return response.value;
  },

  // Fetches a single node's full detail plus its Business/Competency Question traceability,
  // for the Concept View drawer.
  getNodeDetail: async (
    initiativeId: string,
    nodeId: string
  ): Promise<{
    conceptRow: ConceptRow;
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  }> => {
    const response = await OntologyApiClient.getNodeDetail(
      initiativeId,
      nodeId
    );
    const data = response.value;
    return {
      conceptRow: mapApiNodeToConceptRow(data.node),
      businessQuestions: data.traceability.businessQuestions,
      competencyQuestions: data.traceability.competencyQuestions
    };
  },

  // Fetches a single node's own attributes directly (not the paginated, initiative-wide
  // attributeRows table), for the Graph View Node Details panel's Attributes section.
  getNodeAttributes: async (
    initiativeId: string,
    nodeId: string
  ): Promise<AttributeRow[]> => {
    const response = await OntologyApiClient.getNodeRelatedEntities(
      initiativeId,
      nodeId
    );
    const data = response.value;
    const nodeNameById = new Map([[data.node.nodeId, data.node.nodeName]]);
    return data.attributes.map((item) =>
      mapApiAttributeToRow(item, nodeNameById)
    );
  },

  // Fetches a single attribute's full detail plus its Business/Competency Question
  // traceability, for the Attribute View drawer. nodeNameById resolves the attribute's
  // parent nodeId to a display name using the already-loaded Concepts list.
  getAttributeDetail: async (
    initiativeId: string,
    attributeId: string,
    nodeNameById: Map<string, string>
  ): Promise<{
    attributeRow: AttributeRow;
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  }> => {
    const response = await OntologyApiClient.getAttributeDetail(
      initiativeId,
      attributeId
    );
    const data = response.value;
    return {
      attributeRow: mapApiAttributeToRow(data.attribute, nodeNameById),
      businessQuestions: data.traceability.businessQuestions,
      competencyQuestions: data.traceability.competencyQuestions
    };
  },

  // Fetches a single relationship's full detail plus its Business/Competency Question
  // traceability, for the Relationship View drawer. nodeNameById resolves the relationship's
  // sourceNodeId/targetNodeId to display names using the already-loaded Concepts list.
  getRelationshipDetail: async (
    initiativeId: string,
    relationshipId: string,
    nodeNameById: Map<string, string>
  ): Promise<{
    relationshipRow: RelationshipRow;
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  }> => {
    const response = await OntologyApiClient.getRelationshipDetail(
      initiativeId,
      relationshipId
    );
    const data = response.value;
    return {
      relationshipRow: mapApiRelationshipToRow(data.relationship, nodeNameById),
      businessQuestions: data.traceability.businessQuestions,
      competencyQuestions: data.traceability.competencyQuestions
    };
  },

  // Fetches a node's subgraph (itself + directly connected nodes/edges) for the
  // double-click "Related Entities" view on the Graph View canvas.
  getNodeGraphView: async (
    nodeId: string
  ): Promise<OntologyNodeGraphViewResponse> => {
    const response = await OntologyApiClient.getNodeGraphView(nodeId);
    return response.value;
  },

  // Fetches the full Concepts/Relationships graph for an initiative (Graph View tab).
  getGraphView: async (
    initiativeId: string
  ): Promise<{ nodes: GraphNode[]; edges: GraphEdge[] }> => {
    const response = await OntologyApiClient.getGraphView(initiativeId);
    return mapGraphViewResponse(response.value);
  },

  // Patches a single node/attribute/relationship's editable fields (Modify drawer).
  updateEntity: async (
    initiativeId: string,
    entityType: EntityPatchType,
    entityId: string,
    updates: Record<string, string>
  ): Promise<OntologyEntityPatchResponse> => {
    const response = await OntologyApiClient.updateEntity(initiativeId, {
      entityType,
      entityId,
      updates
    });
    return response.value;
  },

  // Creates a new node/attribute/relationship via the real backend (Add drawer).
  createEntity: async (
    initiativeId: string,
    payload: OntologyEntityCreateRequest
  ): Promise<OntologyEntityCreateResponse> => {
    const response = await OntologyApiClient.createEntity(
      initiativeId,
      payload
    );
    return response.value;
  },

  // Queues reuse/harmonisation analysis for an initiative. Fire-and-forget: poll
  // getReuseReviews afterwards until review rows appear.
  triggerReuseHarmonizeAnalysis: async (
    initiativeId: string
  ): Promise<OntologyReuseHarmonizeTriggerResponse> => {
    const response = await OntologyApiClient.triggerReuseHarmonizeAnalysis(
      initiativeId
    );
    return response.value;
  },

  // Fetches reuse/harmonisation review rows (Decision Matrix table) mapped to the UI's
  // ReuseDecisionRow shape, plus the KPI summary cards' counts straight from the backend.
  getReuseReviews: async (
    initiativeId: string,
    page = 1,
    pageSize = 100,
    filters?: OntologyReuseReviewQueryFilters
  ): Promise<{
    rows: ReuseDecisionRow[];
    summary: ReuseHarmonisationSummary;
    totalRecords: number;
    page: number;
    totalPages: number;
  }> => {
    const response = await OntologyApiClient.getReuseReviews(
      initiativeId,
      page,
      pageSize,
      filters
    );
    const data = response.value;
    return {
      rows: data.reviews.map(mapReuseReviewToRow),
      summary: mapReuseSummary(data.summary, data.totalRecords, data.reviews),
      totalRecords: data.totalRecords,
      page: data.page,
      totalPages: data.totalPages
    };
  },

  // Fetches a single candidate match's rationale text (used by the history drawer as a
  // fallback when a match's rationale wasn't already included in the reviews list).
  getReuseMatchRationale: async (
    initiativeId: string,
    matchId: string
  ): Promise<string | null> => {
    const response = await OntologyApiClient.getReuseMatchRationale(
      initiativeId,
      matchId
    );
    return response.value.rationale;
  },

  // Applies explicit decisions in bulk, optionally auto-accepting the AI recommendation for
  // every undecided row (acceptAll), then returns the updated summary counts.
  setReuseDecisionsBulk: async (
    initiativeId: string,
    decisions: OntologyReuseDecisionItemRequest[],
    acceptAll = false
  ): Promise<OntologyReuseBulkDecisionResponse> => {
    const response = await OntologyApiClient.setReuseDecisionsBulk(
      initiativeId,
      {
        decisions,
        acceptAll
      }
    );
    return response.value;
  },

  // Sets Reuse/Harmonise (with matchId) or Add to Initiative for a single review row.
  setReuseDecision: async (
    initiativeId: string,
    reviewId: string,
    userDecision: string,
    matchId?: string
  ): Promise<OntologyReuseDecisionResponse> => {
    const response = await OntologyApiClient.setReuseDecision(
      initiativeId,
      reviewId,
      userDecision,
      matchId
    );
    return response.value;
  }
};
